/**
 * Cookie utility functions for managing browser cookies
 */

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

/**
 * Set a cookie with specified options
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options
 * @param {number} options.maxAge - Max age in seconds
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain
 * @param {boolean} options.secure - Secure flag
 * @param {string} options.sameSite - SameSite attribute
 */
export const setCookie = (name, value, options = {}) => {
  const {
    maxAge,
    path = '/',
    domain,
    secure,
    sameSite = 'Lax'
  } = options;

  let cookieString = `${name}=${value}`;
  
  if (maxAge !== undefined) {
    cookieString += `; Max-Age=${maxAge}`;
  }
  
  cookieString += `; Path=${path}`;
  
  if (domain) {
    cookieString += `; Domain=${domain}`;
  }
  
  if (secure) {
    cookieString += `; Secure`;
  }
  
  cookieString += `; SameSite=${sameSite}`;
  
  document.cookie = cookieString;
};

/**
 * Delete a cookie by name with all possible path and domain combinations
 * @param {string} name - Cookie name to delete
 */
export const deleteCookie = (name) => {
  // Common paths and domains to try when deleting cookies
  const paths = ['/', '/auth', ''];
  const domains = [
    window.location.hostname,
    `.${window.location.hostname}`,
    window.location.hostname.split('.').slice(-2).join('.'), // Get main domain
    `.${window.location.hostname.split('.').slice(-2).join('.')}`, // Get main domain with dot
    '', // No domain specified
  ];

  // Remove duplicates and filter out invalid domains
  const uniqueDomains = [...new Set(domains)].filter(domain => 
    domain === '' || domain.includes('.')
  );

  // Try deleting with all combinations of paths and domains
  paths.forEach(path => {
    uniqueDomains.forEach(domain => {
      const cookieString = domain 
        ? `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`
        : `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
      
      document.cookie = cookieString;
    });
  });

  // Also try without specifying path or domain
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
};

/**
 * Clear only the refresh_token cookie
 * This function specifically targets only the refresh_token cookie
 */
export const clearAuthCookies = () => {
  deleteCookie('refresh_token');
};

/**
 * Clear all cookies (nuclear option)
 * Use this only when you need to clear everything
 */
export const clearAllCookies = () => {
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.trim().split('=');
    if (name) {
      deleteCookie(name);
    }
  });
};

/**
 * Check if a specific auth cookie exists
 * @param {string} cookieName - Name of the cookie to check
 * @returns {boolean} True if cookie exists
 */
export const hasAuthCookie = (cookieName) => {
  return getCookie(cookieName) !== null;
};

/**
 * Get all current cookies as an object
 * @returns {Object} Object with cookie names as keys and values as values
 */
export const getAllCookies = () => {
  const cookies = {};
  document.cookie.split(';').forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[name] = decodeURIComponent(value);
    }
  });
  return cookies;
};

/**
 * Debug function to log all current cookies
 * Useful for development and debugging auth issues
 */
export const debugCookies = () => {
  const cookies = getAllCookies();
  console.group('🍪 Current Cookies');
  console.log('All cookies:', cookies);

  const authCookies = Object.keys(cookies).filter(name =>
    name.toLowerCase().includes('token') ||
    name.toLowerCase().includes('auth')
  );

  if (authCookies.length > 0) {
    console.log('Auth-related cookies:',
      authCookies.reduce((acc, name) => {
        acc[name] = cookies[name];
        return acc;
      }, {})
    );
  } else {
    console.log('No auth-related cookies found');
  }
  console.groupEnd();
};

/**
 * Get the refresh token from cookies
 * Tries multiple possible cookie names
 * @returns {string|null} Refresh token value or null if not found
 */
export const getRefreshToken = () => {
  const possibleNames = ['refresh_token', 'refresh-token', 'refreshToken'];

  for (const name of possibleNames) {
    const token = getCookie(name);
    if (token) {
      return token;
    }
  }

  return null;
};

/**
 * Check if user has valid auth cookies
 * @returns {boolean} True if any auth token cookies are present
 */
export const hasValidAuthCookies = () => {
  return getRefreshToken() !== null || hasAuthCookie('auth-token');
};

/**
 * Force clear all cookies with extreme prejudice
 * This is a nuclear option that tries every possible combination
 * Use this if the regular clearAuthCookies doesn't work
 */
export const forceCleanAllCookies = () => {
  console.log('🧹 Force cleaning all cookies...');

  // Get all current cookies
  const allCookies = getAllCookies();
  const cookieNames = Object.keys(allCookies);

  console.log('Found cookies:', cookieNames);

  // Try to delete each cookie with multiple strategies
  cookieNames.forEach(name => {
    console.log(`Deleting cookie: ${name}`);
    deleteCookie(name);

    // Additional aggressive deletion attempts
    const aggressivePaths = ['/', '/auth', '/api', ''];
    const aggressiveDomains = [
      '',
      window.location.hostname,
      `.${window.location.hostname}`,
      'localhost',
      '.localhost'
    ];

    aggressivePaths.forEach(path => {
      aggressiveDomains.forEach(domain => {
        try {
          if (domain) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; secure`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}`;
          } else {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; secure`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
          }
        } catch (e) {
          // Ignore errors, some combinations might not be valid
        }
      });
    });
  });

  // Final verification
  const remainingCookies = getAllCookies();
  const remaining = Object.keys(remainingCookies);

  if (remaining.length === 0) {
    console.log('✅ All cookies successfully cleared!');
  } else {
    console.warn('⚠️ Some cookies remain:', remaining);
    console.log('Remaining cookies:', remainingCookies);
  }

  return remaining.length === 0;
};

// Make some functions available globally for debugging in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.cookieUtils = {
    debugCookies,
    clearAuthCookies,
    forceCleanAllCookies,
    getAllCookies,
    getCookie,
    deleteCookie,
    hasValidAuthCookies,
    getRefreshToken
  };
}
